/* <country-selector>: the country/region sheet (snippet country-selector) on
 * the shared drawer-dialog controller. A plain click on any [data-country-open]
 * link opens it; a modified click keeps the link, and a page opened at
 * #country-selector starts with the sheet open. Focus goes to the search on a
 * fine pointer and to the close button on touch, so no keyboard pops up.
 * Search matches the start of any word of the name or a common alias, without
 * accents, or the exact ISO or currency code; groups without a match hide and
 * a polite status reads the count. Keys: ArrowDown from the field to the first
 * match, ArrowUp/ArrowDown/Home/End between matches, a letter back into the
 * field, Enter in the field submits the first match (never a hidden country:
 * the field is outside the form). Escape with a search typed clears it and
 * returns to the field, wherever focus is in the sheet; the next one closes. */
// Defined only with the shared controller in place: until then the links' in-page target stays.
if (!customElements.get('country-selector') && window.AsansaraDrawer) {
  // Names people type that Shopify spells differently, by ISO code.
  const ALIASES = {
    AE: ['UAE', 'Emirates', 'Dubai'],
    CH: ['Swiss'],
    CZ: ['Czech Republic'],
    GB: ['UK', 'Britain', 'England'],
    KR: ['Korea'],
    NL: ['Holland'],
    TR: ['Turkey'],
    US: ['USA', 'America'],
  };
  const normalize = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

  customElements.define('country-selector', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.drawer = new window.AsansaraDrawer(this.dialog, '[data-country-close]');
      this.input = this.querySelector('[data-country-search]');
      this.groups = this.querySelector('[data-country-groups]');
      this.empty = this.querySelector('[data-country-empty]');
      this.status = this.querySelector('[data-country-status]');
      this.plural = new Intl.PluralRules(document.documentElement.lang || 'en');
      this.countries = Array.from(this.querySelectorAll('button[name="country_code"]'), (button) => {
        const names = [button.querySelector('.country-selector__name').textContent, ...(ALIASES[button.value] || [])].map(normalize);
        return {
          button,
          item: button.parentElement,
          names,
          words: names.flatMap((name) => name.split(/[\s-]+/)),
          codes: [button.value, button.dataset.currency].map(normalize),
        };
      });

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-country-open]');
        if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger);
      }, options);
      this.input.addEventListener('input', () => this.filter(), options);
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.dialog.addEventListener('close', () => {
        this.input.value = '';
        this.filter();
        if (location.hash === `#${this.dialog.id}`) history.replaceState(history.state, '', location.pathname + location.search);
      }, options);

      if (location.hash === `#${this.dialog.id}`) this.open();
    }

    disconnectedCallback() {
      this.events.abort();
      this.drawer?.destroy();
    }

    open(opener) {
      const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.drawer.open(opener, fine ? this.input : null);
    }

    visible() {
      return this.countries.filter((country) => !country.item.hidden).map((country) => country.button);
    }

    filter() {
      const query = normalize(this.input.value);
      let shown = 0;
      this.countries.forEach((country) => {
        const match = !query || country.codes.includes(query)
          || country.names.some((name) => name.startsWith(query))
          || country.words.some((word) => word.startsWith(query));
        country.item.hidden = !match;
        if (match) shown += 1;
      });
      this.querySelectorAll('[data-country-group]').forEach((group) => {
        group.hidden = !group.querySelector('li:not([hidden])');
      });
      this.groups.classList.toggle('is-filtering', Boolean(query));
      this.empty.hidden = shown > 0;
      if (!query) this.status.textContent = '';
      else if (!shown) this.status.textContent = this.empty.textContent;
      else {
        const template = this.plural.select(shown) === 'one' ? this.status.dataset.countOne : this.status.dataset.countOther;
        this.status.textContent = template.replace('[count]', shown);
      }
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape' && this.input.value) {
        event.preventDefault(); // no cancel event: the first Escape only clears, from the field or a match
        this.input.value = '';
        this.filter();
        this.input.focus();
        return;
      }
      const matches = this.visible();
      if (event.target === this.input) {
        if (event.key === 'ArrowDown' && matches.length) {
          event.preventDefault();
          matches[0].focus();
        } else if (event.key === 'Enter') {
          event.preventDefault();
          if (normalize(this.input.value)) matches[0]?.click();
        }
        return;
      }
      const index = matches.indexOf(event.target);
      if (index < 0) return;
      const next = {
        ArrowDown: matches[index + 1],
        ArrowUp: index ? matches[index - 1] : this.input,
        Home: matches[0],
        End: matches[matches.length - 1],
      }[event.key];
      if (next) {
        event.preventDefault();
        next.focus();
      } else if (event.key.length === 1 && event.key !== ' ' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        this.input.focus(); // the typed letter lands in the field
      }
    }
  });
}
